import * as React from 'react';
import { Link } from 'react-router';
import styled, { withTheme } from 'styled-components';
import { rem, darken } from 'polished';
import { TextP } from '../../Defaults/Text';
import {
  FlexCol,
  FlexBetween,
  FlexRowCenterVertical,
} from '../../Defaults/Flexbox/Flexbox';
import { __, __r } from 'react-i18n';
import { TableTd, TableRow, DefaultTableTd } from '../../Zemplin/General/Table';
import {
  getInvoiceDetailInfo,
  getOrderItemRowInfo,
  getOrderRowInfo,
} from '../../../utilities/selectors';
import { SvgIcon, IconType } from '../../Zemplin/General/SvgIcon';
import {
  formatDate,
  formatPrice,
  getOrderColor,
  prefixUrl,
  prop,
} from '../../../utilities';
import { getImagePath } from '../../../utilities/product';
import { StockInfo } from '../Product/StockInfo';

export interface Props {
  order?: any;
  theme: any;
  color?: string;
  currency: string;
  user: any;
}

function BackorderRow({ order, theme, color = '', currency, user }: Props) {
  const {
    id,
    date,
    status,
    sumPrice,
    ordernr,
    picture,
    name,
    plu,
    availability,
    availid,
  } = getOrderItemRowInfo(order ? order.product : {});
  // const color = getOrderColor(status, theme);
  const productLink = 
     prefixUrl('/',prop(order, 'product.publish.0.product_url') || prop(order, 'product.url'));

  const renderImage = () => {
    return (
      <ImageWrapper>
        <Image
          width={56}
          height={56}
          alt={`Obrázok produktu`}
          src={
            picture
              ? getImagePath(picture, { width: 56, height: 56 })
              : '/images/icons/placeholder.svg'
          }
        />
      </ImageWrapper>
    );
  };

  const renderWithLink = (link: string, row) => {
    if (link) {
      return <Link to={link}>{row}</Link>;
    } else {
      return row;
    }
  };
  return (
    <StyledTableRow key={prop(order, '@_OriCus_OrdCode')}>
      <StyledTableTd>
        <FlexRowCenterVertical>
          <ImageLinkWrapper>
            {renderWithLink(
              productLink,
              <ImageWrapper>
                <Image
                  width={56}
                  height={56}
                  alt={`Obrázok produktu`}
                  src={
                    picture
                      ? getImagePath(picture, { width: 56, height: 56 })
                      : '/images/icons/placeholder.svg'
                  }
                />
              </ImageWrapper>,
            )}
          </ImageLinkWrapper>
          <FlexCol>
            {renderWithLink(
              productLink,
              <ProductName color={color}>{name}</ProductName>,
            )}
            {renderWithLink(
              productLink,
              <>
                <ProductPlu>{plu}</ProductPlu>{' '}
              </>,
            )}
            {user && user.originalUser ? (
              <StockInfo availability={availability} avail_id={availid} />
            ) : null}
            {user && user.originalUser ? (
              <a
                href={`/manager?customerId=${user.outer_id}-${user.id}&customerAddressId=&query=${plu}`}
                target="_blank"
              >
                Report
              </a>
            ) : null}
          </FlexCol>
        </FlexRowCenterVertical>
      </StyledTableTd>
      <StyledTableTd>
        <Link
          to={`${__r(
            'routes:moj-ucet/objednavka',
            '/moj-ucet/objednavka',
          )}/${prop(order, '@_OriCus_OrdId')}`}
        >
          <StatusTextOrderCode color={color}>
            {prop(order, '@_OriCus_OrdCode')}
          </StatusTextOrderCode>
        </Link>
      </StyledTableTd>
      <StyledTableTd>
        <StatusText color={color}>
          {formatDate(prop(order, '@_OriCus_C'), 'DD-MM-YYYY')}
        </StatusText>
      </StyledTableTd>
      <StyledTableTd>
        <StatusTextCenter color={color} align={false}>{`${prop(
          order,
          'qty',
        )} / ${prop(order, '@_OriCus_Block')} ${__('ks')}`}</StatusTextCenter>
      </StyledTableTd>
      <StyledTableTd>
        {' '}
        <StatusTextCenter color={color} align={false}>{`${prop(
          order,
          'totalBackOrdered',
        )} / ${prop(order, 'totalBlocked')} ${__('ks')}`}</StatusTextCenter>
      </StyledTableTd>
      <StyledTableTd>
        {' '}
        <StatusText color={color} align={true}>
          {formatPrice(prop(order, 'totalPrice'), currency)}
        </StatusText>
      </StyledTableTd>
    </StyledTableRow>
  );
}

const StyledTableTd = styled(TableTd)<{ align: boolean }>`
  padding: ${rem(16)} ${rem(24)};
  border-bottom: ${({ theme }) => theme.borders.primary};
`;

const StyledTableRow = styled(TableRow)`
  padding: ${rem(24)} 0;
  border-top: ${({ theme }) => theme.borders.primary};
  background-color: ${({ theme }) => theme.colors.white};
  &:last-child {
    border-bottom: ${({ theme }) => theme.borders.primary};
  }
`;

const StatusText = styled(TextP)<{ color?: string; align?: boolean }>`
  font-size: ${rem(16)};
  line-height: ${rem(22)};
  color: ${({ theme, color }) =>
    color === 'red' ? theme.colors.dangerColor : theme.colors.textPrimary};
  font-weight: ${({ color }) => (color === 'red' ? '500' : '400')};
  text-align: ${({ align }) => (align ? 'right' : 'left')};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StatusTextCenter = styled(TextP)<{ color?: string; align?: boolean }>`
  font-size: ${rem(16)};
  line-height: ${rem(22)};
  color: ${({ theme, color }) =>
    color === 'red' ? theme.colors.dangerColor : theme.colors.textPrimary};
  font-weight: ${({ color }) => (color === 'red' ? '500' : '400')};
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StatusTextOrderCode = styled(StatusText)`
  &:hover {
    text-decoration: underline;
  }
`;

const ProductName = styled(TextP)`
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

const ProductPlu = styled(TextP)`
  font-size: ${rem(14)};
  color: #aaaaaa;

  &:hover {
    text-decoration: underline;
  }
`;

const ImageWrapper = styled(FlexCol)`
  flex-shrink: 0;
  margin-right: ${rem(16)};
`;

const Image = styled.img`
  flex-shrink: 0;
  margin-right: ${rem(16)};
`;

const ImageLinkWrapper = styled.div`
  flex-shrink: 0;
  display: flex;
`


const WithThemeRow: any = withTheme(BackorderRow);
export { WithThemeRow as DsiBackorderRow };
